/* eslint-disable curly */
/* eslint-disable max-len */
import { TWidget } from 'widgets/Widget';
import { isLargeViewAndUp, isMediumView, isSmallView } from 'widgets/toolbox/viewtype';

function ScrollCarouselClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory plugin_page_designer_ext
     * @class ScrollCarousel
     * @augments Widget
     * @classdesc Represents horizontal scroll-based carousel component with next features:
     * 1. Allows to define number of visible slides per viewport (xs / sm / md) via data attributes
     * 2. Calculates slide width dynamically and exposes it via CSS custom property (--slide-width)
     * 3. Allows to scroll carousel to the next or previous page (page = visible container width)
     * 4. Supports smooth horizontal scrolling using native browser scroll
     * 5. Supports navigation via control buttons (previous / next)
     *
     * @property {string} data-widget - Widget name `scrollCarousel`
     * @property {string} data-xs - Number of slides visible on extra-small viewport
     * @property {string} data-sm - Number of slides visible on small viewport
     * @property {string} data-md - Number of slides visible on medium and up viewport
     * @property {string} data-number-of-slides - Total number of slides in carousel
     *
     * @example <caption>Example of ScrollCarousel widget usage</caption>
     * <div
     *     class="b-scroll-carousel ${pdict.deviceVisibility}
     *         ${pdict.displayControls.xs} ${pdict.displayControls.sm} ${pdict.displayControls.md}
     *         ${pdict.fullScreenWidth ? 'component--full-screen-width' : ''}"
     *     data-widget="scrollCarousel"
     *     data-next-slide-visible="${pdict.nextSlideVisible}"
     *     data-xs="1"
     *     data-sm="2"
     *     data-md="4">
     *
     *     <div class="b-scroll-carousel_slides">
     *         <div class="b-scroll-carousel_slide">Slide 1</div>
     *         <div class="b-scroll-carousel_slide">Slide 2</div>
     *         <div class="b-scroll-carousel_slide">Slide 3</div>
     *         <div class="b-scroll-carousel_slide">Slide 4</div>
     *         <div class="b-scroll-carousel_slide">Slide 5</div>
     *         <div class="b-scroll-carousel_slide">Slide 6</div>
     *     </div>
     *
     *    <div class="b-scroll-carousel-scrollbar">
     *        <div class="b-scroll-carousel-scrollbar-thumb"></div>
     *    </div>
     *
     *     <button
     *         class="b-scroll-carousel-control-prev"
     *         data-event-click="scrollBack">
     *         <span class="b-scroll-carousel-control-prev-icon" aria-hidden="true"></span>
     *         <span class="sr-only">${Resource.msg('button.previous', 'common', null)}</span>
     *     </button>
     *
     *     <button
     *         class="b-scroll-carousel-control-next"
     *         data-event-click="scrollForward">
     *         <span class="b-scroll-carousel-control-next-icon" aria-hidden="true"></span>
     *         <span class="sr-only">${Resource.msg('button.next', 'common', null)}</span>
     *     </button>
     * </div>
     */

    class ScrollCarousel extends Widget {
        carousel: HTMLElement | null = null;

        slidesContainer: HTMLElement | null = null;

        controls: HTMLElement | null = null;

        scrollbar: HTMLElement | null = null;

        thumb: HTMLElement | null = null;

        btnBack: HTMLElement | null = null;

        btnForward: HTMLElement | null = null;

        nextSlideVisible = false;

        prefs() {
            return {
                slidesSelector: '.b-scroll-carousel_slides',
                controlsSelector: '.b-scroll-carousel-controls',
                scrollbarSelector: '.b-scroll-carousel-scrollbar',
                btnBackSelector: '.b-scroll-carousel-control-prev',
                btnForwardSelector: '.b-scroll-carousel-control-next',
                thumbSelector: '.b-scroll-carousel-scrollbar-thumb',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.carousel = super.ref('self').get() as HTMLElement;
            this.slidesContainer = this.carousel.querySelector(this.prefs().slidesSelector);

            if (!this.removeEmptySlides()) {
                this.carousel.remove();

                return;
            }

            this.controls = this.carousel.querySelector(this.prefs().controlsSelector);
            this.scrollbar = this.carousel.querySelector(this.prefs().scrollbarSelector);
            this.thumb = this.carousel.querySelector(this.prefs().thumbSelector);

            this.btnBack = this.carousel.querySelector(this.prefs().btnBackSelector);
            this.btnForward = this.carousel.querySelector(this.prefs().btnForwardSelector);

            this.nextSlideVisible = isSmallView()
                ? this.carousel.dataset.nextSlideVisibleXs === 'true'
                : this.carousel.dataset.nextSlideVisibleMd === 'true';

            this.setSlideWidth();
            this.updateOverflowVisibility();
            this.updateControls();
            this.updateThumb();
            this.initEvents();
        }

        /**
         * @description Remove empty slide wrappers (e.g. offline product tiles that returned '').
         * Returns true when at least one slide remains.
         */
        private removeEmptySlides(): boolean {
            if (!this.slidesContainer) return false;

            Array.from(this.slidesContainer.children).forEach((node) => {
                if (!(node instanceof HTMLElement)) return;
                if (!node.classList.contains('b-scroll-carousel_slide')) return;
                if (!node.innerHTML.trim()) node.remove();
            });

            return !!this.slidesContainer.querySelector('.b-scroll-carousel_slide');
        }

        /**
         * @description Resolve slides count from dataset depending on viewport
         */
        private getSlidesToShow(): number | null {
            // early return
            if (!this.carousel) return null;

            const { md, sm, xs } = this.carousel.dataset;

            if (isLargeViewAndUp()) return Number(md) || null;
            if (isMediumView()) return Number(sm) || null;

            return Number(xs) || null;
        }

        /**
         * @description Calculate and apply slide width based on current viewport
         */
        private setSlideWidth() {
            // early return
            if (!this.carousel) return;

            const slidesToShow = this.getSlidesToShow();

            if (slidesToShow) {
                const nextSlideFraction = this.nextSlideVisible ? 1 / 2 : 0;

                this.carousel.style.setProperty('--slide-width', `${100 / (slidesToShow + nextSlideFraction)}%`);
            }
        }

        /**
         * @description Update visibility of carousel navigation controls based on current scroll position
         */
        private updateControls() {
            // early return
            if (!this.slidesContainer) return;

            const container = this.slidesContainer;
            const maxScroll = Math.round(container.scrollWidth - container.clientWidth);
            const pos = Math.round(container.scrollLeft);

            this.btnBack?.toggleAttribute('disabled', pos === 0);
            this.btnForward?.toggleAttribute('disabled', pos === maxScroll);
        }

        /**
         * @description Show controls and scrollbar only when horizontal overflow exists
         */
        private updateOverflowVisibility() {
            // early return
            if (!this.slidesContainer) return;

            const hasOverflow = this.slidesContainer.scrollWidth > this.slidesContainer.clientWidth;

            this.controls?.toggleAttribute('hidden', !hasOverflow);
            this.scrollbar?.toggleAttribute('hidden', !hasOverflow);
        }

        /**
         * @description Update thumb in custom scroll
         */
        private updateThumb() {
            // early return
            if (!this.carousel || !this.slidesContainer || !this.thumb) return;

            const containerWidth = this.carousel.clientWidth;
            const contentWidth = this.slidesContainer.scrollWidth;
            const scrollLeft = this.slidesContainer.scrollLeft;

            if (contentWidth <= containerWidth) {
                this.thumb.style.width = `${containerWidth}px`;
                this.thumb.style.left = '0px';

                return;
            }

            const thumbWidth = Math.max((containerWidth / contentWidth) * containerWidth, 30);
            const thumbLeft = (scrollLeft / (contentWidth - containerWidth)) * (containerWidth - thumbWidth);

            this.thumb.style.width = `${thumbWidth}px`;
            this.thumb.style.left = `${thumbLeft}px`;
        }

        /**
         * @description Bound scroll event handler
         */
        private onScroll = () => {
            this.updateOverflowVisibility();
            this.updateControls();
            this.updateThumb();
        };

        /**
         * @description Recalculate carousel layout after viewport changes
         */
        private onResize = () => {
            this.setSlideWidth();
            this.updateOverflowVisibility();
            this.updateControls();
            this.updateThumb();
        };

        /**
         * @description Scroll carousel by one visible page
         * @param delta Signed scroll delta in pixels
         */
        private scrollByPage(delta: number) {
            // early return
            if (!this.slidesContainer) return;

            const container = this.slidesContainer;
            const maxScroll = Math.round(container.scrollWidth - container.clientWidth);
            const current = Math.round(container.scrollLeft);
            const step = Math.round(delta);
            let target = current + step;

            target = Math.min(Math.max(target, 0), maxScroll);

            container.scrollTo({
                left: target,
                behavior: 'smooth'
            });
        }

        /**
         * @description Init carousel events
         */
        initEvents() {
            // defaults
            let isDragging = false;
            let startX = 0;
            let startLeft = 0;

            const startDrag = (e: PointerEvent) => {
                // early return
                if (!this.thumb || !this.carousel) return;

                isDragging = true;
                startX = e.clientX;
                startLeft = parseFloat(window.getComputedStyle(this.thumb).left);

                this.thumb.setPointerCapture(e.pointerId);

                document.body.style.userSelect = 'none';
            };

            const onDrag = (e: PointerEvent) => {
                // early return
                if (!isDragging || !this.thumb || !this.carousel || !this.slidesContainer) return;

                const delta = e.clientX - startX;
                const maxLeft = this.carousel.clientWidth - this.thumb.clientWidth;
                const newLeft = Math.min(Math.max(startLeft + delta, 0), maxLeft);

                this.thumb.style.left = `${newLeft}px`;

                const scrollRatio = newLeft / maxLeft;

                this.slidesContainer.scrollLeft = scrollRatio * (this.slidesContainer.scrollWidth - this.carousel.clientWidth);
            };

            const endDrag = (e: PointerEvent) => {
                isDragging = false;

                this.thumb?.releasePointerCapture(e.pointerId);

                document.body.style.userSelect = '';
            };

            this.slidesContainer?.addEventListener('scroll', this.onScroll, { passive: true });
            this.thumb?.addEventListener('pointerdown', startDrag);
            window.addEventListener('resize', this.onResize);

            document.addEventListener('pointermove', onDrag);
            document.addEventListener('pointerup', endDrag);
        }

        /**
         * @description Scroll carousel to the next page
         * @listens dom#click
         */
        scrollForward() {
            this.scrollByPage(this.slidesContainer?.clientWidth ?? 0);
        }

        /**
         * @description Scroll carousel to the previous page
         * @listens dom#click
         */
        scrollBack() {
            this.scrollByPage(-(this.slidesContainer?.clientWidth ?? 0));
        }
    }

    return ScrollCarousel;
}

export type TScrollCarousel = ReturnType<typeof ScrollCarouselClassCreator>;

export type TScrollCarouselInstance = InstanceType<TScrollCarousel>;

export default ScrollCarouselClassCreator;
